import { yupResolver } from '@hookform/resolvers/yup';
import { CustomPageType, HeaderCustomization, IOperationResultNoValue, NavLink } from '@ViewModels';
import { css } from 'aphrodite';
import equal from 'fast-deep-equal';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { FormProvider, SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import { useModal } from '../../../../../models/hooks/useModal';
import { useRemoveAccountLogoMutation, useUploadAccountLogoMutation } from '../../../../../queries';
import { invalidateAccountAdminQuery } from '../../../../../queries/Admin/Account/useAdminAccountQuery';
import { invalidateAllHostedBlogPagePreviews } from '../../../../../queries/HostedBlog/useHostedBlogPagePreviewQuery';
import { invalidateSchedulerPreviewQuery } from '../../../../../queries/Scheduler/useSchedulerPreviewQuery';
import { invalidateSurveyPreviewQuery } from '../../../../../queries/Surveys/useSurveyPreviewQuery';
import { useDeleteBlogStyleMutation } from '../../../../../queries/CustomPageStyle/useDeleteBlogStyleMutation';
import { useFindOrCreateAndUpdateBlogStyleMutation } from '../../../../../queries/CustomPageStyle/useFindOrCreateAndUpdateBlogStyleMutation';
import { ColorPicker } from '../../../../../web/components-v2/ColorPicker';
import {
	ConfirmationDialog,
	DefaultConfirmationOptions,
	DefaultDeleteConfirmationOptions,
} from '../../../../../web/components/ConfirmationDialog';
import { ImageFileChooserModal } from '../../../../../web/components/ImageFileChooserModal';
import { LoadingSpinner } from '../../../../../web/components/LoadingSpinner';
import { WarningIcon } from '../../../../../web/components/svgs/icons/WarningIcon';
import { baseStyleSheet, bs } from '../../../../../web/styles/styles';
import { useAccountContext } from '../../../../components/blogs/AccountContext';
import {
	AppendInputRow,
	InputFormTable,
	InputFormTableRow,
	InputFormTableRowChildren,
} from '../../../../components/blogs/InputFormTable';
import { styleSheet } from '../../../../components/blogs/styles';
import { FontSelect } from '../../../../components/FontSelect/FontSelect';
import { InfoPopover } from '../../../../components/InfoPopover/InfoPopover';
import { LabeledInput } from '../../../../components/LabeledInput/LabeledInput';
import { Column, Row } from '../../../../components/Layout';
import { applyPresetToHeaderForm } from '../applyPreset';
import { StylePreset } from '../presets';
import { Toggle } from '../../../../../web/components/Toggle';
import { DEFAULT_HEADER_FORM_VALUES, DEFAULT_NAV_LINK_VALUES, MAX_FONT_SIZE_PX, MIN_FONT_SIZE_PX } from './constants';
import { FeatureToggleSection } from './FeatureToggleSection';
import { ResponsiveFontSizeInputs } from './ResponsiveFontSizeInputs';
import { headerSchema } from './schemas';
import { TemplateFormWrapper } from './TemplateFormWrapper';

interface HeaderDesignFormProps {
	customContent?: HeaderCustomization;
	onUpdateSuccess: () => void;
	onFormDirty: () => void;
}

export interface HeaderDesignFormRef {
	applyPreset: (preset: StylePreset) => void;
}

const parseOptionalFontSize = (value: string) => {
	if (value === '' || value === null || value === undefined) {
		return undefined;
	}

	const parsedValue = Number.parseInt(value, 10);
	return Number.isNaN(parsedValue) ? undefined : parsedValue;
};

const getFontSizePlaceholder = (fontSizePx?: number, fallbackFontSizePx?: number) => {
	if (fontSizePx !== undefined && fontSizePx !== null) {
		return String(fontSizePx);
	}

	return fallbackFontSizePx !== undefined ? String(fallbackFontSizePx) : 'Native';
};

export const HeaderDesignForm = forwardRef<HeaderDesignFormRef, HeaderDesignFormProps>(
	({ customContent, onFormDirty, onUpdateSuccess }, ref) => {
		const errorMessages = useErrorMessages();
		const { account, accountId, impersonationContext } = useAccountContext();
		const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
		const [showLogoFileInputModal, setShowLogoFileInputModal] = useState(false);

		// Logo mutations
		const uploadLogoMutation = useUploadAccountLogoMutation({
			onSuccess: () => {
				invalidateAccountAdminQuery();
				invalidateAllHostedBlogPagePreviews();
				invalidateSurveyPreviewQuery();
				invalidateSchedulerPreviewQuery();
				setShowLogoFileInputModal(false);
			},
			onError: error => {
				setShowLogoFileInputModal(false);
				errorMessages.pushApiError(error);
			},
		});

		const removeLogoMutation = useRemoveAccountLogoMutation({
			onSuccess: () => {
				invalidateAccountAdminQuery();
				invalidateAllHostedBlogPagePreviews();
				invalidateSurveyPreviewQuery();
				invalidateSchedulerPreviewQuery();
			},
			onError: error => {
				errorMessages.pushApiError(error);
			},
		});

		const deleteLogoConfirmationModal = useModal(false, () => {
			removeLogoMutation.mutate({ impersonationContext });
		}, [removeLogoMutation, impersonationContext]);

		const logoUrl = account?.preferences?.logo?.url;
		const isLogoLoading = uploadLogoMutation.isPending || removeLogoMutation.isPending;

		// Determine which features are available for this account
		const blogFeature = account?.features?.blogFeature;
		const blogFeatureAvailable = !!blogFeature?.enabled && !blogFeature?.dudaSiteId;
		const surveyFeatureAvailable = !!account?.features?.surveys?.enabled;
		const meetingFeatureAvailable = !!account?.features?.meetingScheduler?.enabled;

		const form = useForm<HeaderCustomization>({
			defaultValues: DEFAULT_HEADER_FORM_VALUES,
			mode: 'onBlur',
			resolver: yupResolver(headerSchema),
		});
		const {
			control,
			formState: { errors, isDirty },
			handleSubmit,
			register,
			setValue,
			getValues,
			watch,
		} = form;
		const { append, fields, remove } = useFieldArray({ control, name: 'menuLinks' });
		const overallFont = watch('overallFont', {
			fontName: '',
			fontUri: '',
		});
		const dropdownFont = watch('dropdown.font', {
			fontName: '',
			fontUri: '',
		});
		const dropdownFontSize = watch('dropdown.fontSizePx');

		// Watch color values for live updating
		const textColor = watch('textColor');
		const backgroundColor = watch('backgroundColor');
		const hoverColor = watch('hoverColor');
		const dropdownTextColor = watch('dropdown.color');
		const dropdownBackgroundColor = watch('dropdown.backgroundColor');

		// Watch enabledFor for feature toggles
		const enabledFor = watch('enabledFor');

		// Watch logoOnlyHeader so the menu items table can be hidden when it's on.
		const logoOnlyHeader = watch('logoOnlyHeader');
		const watchedMenuLinks = watch('menuLinks');
		const hasNonEmptyMenuLinks = !!watchedMenuLinks?.some(link => !!link?.label || !!link?.destination);

		const handleEnabledForChange = useCallback(
			(newEnabledFor: CustomPageType[]) => {
				setValue('enabledFor', newEnabledFor, { shouldDirty: true });
			},
			[setValue]
		);

		const handleLogoOnlyHeaderChange = useCallback(
			(checked: boolean) => {
				setValue('logoOnlyHeader', checked, { shouldDirty: true });
				if (checked) {
					// Hide and clear any in-memory menu link rows; user can re-add links if they turn the toggle off.
					setValue('menuLinks', [], { shouldDirty: true });
				}
			},
			[setValue]
		);

		const updateHeaderMutation = useFindOrCreateAndUpdateBlogStyleMutation({
			onSuccess: () => {
				onUpdateSuccess();
				toast.success('Saved changes successfully');
			},
			onError: (error: IOperationResultNoValue) => {
				errorMessages.pushApiError(error);
			},
		});

		const deleteHeaderMutation = useDeleteBlogStyleMutation({
			onSuccess: () => {
				form.reset(DEFAULT_HEADER_FORM_VALUES);
				onUpdateSuccess();
				toast.success('Customization deleted successfully');
				setShowDeleteDialog(false);
			},
			onError: (error: IOperationResultNoValue) => {
				errorMessages.pushApiError(error);
				setShowDeleteDialog(false);
			},
		});

		const onValid: SubmitHandler<HeaderCustomization> = (data, event) => {
			event.preventDefault();
			event.stopPropagation();

			updateHeaderMutation.mutate({
				id: accountId,
				impersonationContext,
				style: data,
			});
		};

		const handleDeleteCustomization = () => {
			setShowDeleteDialog(true);
		};

		const handleDeleteConfirm = (_: never, canceled: boolean) => {
			if (!canceled && customContent && customContent.id) {
				deleteHeaderMutation.mutate({
					id: customContent.id,
					impersonationContext,
				});
			} else {
				setShowDeleteDialog(false);
			}
		};

		useEffect(() => {
			if (customContent && !equal(customContent, getValues())) {
				Object.entries(customContent).forEach(entry => {
					if (entry[1] !== undefined && entry[1] !== null) {
						setValue(entry[0] as Extract<keyof HeaderCustomization, string>, entry[1]);
					}
				});
			}
		}, [customContent, getValues, setValue]);

		useEffect(() => {
			if (isDirty) {
				onFormDirty();
			}
		}, [isDirty, onFormDirty]);

		useImperativeHandle(ref, () => ({
			applyPreset: (preset: StylePreset) => {
				const currentValues = getValues();
				const updatedValues = applyPresetToHeaderForm(preset, currentValues);

				(Object.keys(updatedValues) as (keyof HeaderCustomization)[]).forEach(key => {
					const value = updatedValues[key];
					if (value !== undefined) {
						setValue(key, value, { shouldDirty: true });
					}
				});
			},
		}));

		return (
			<>
				<FormProvider {...form}>
					<TemplateFormWrapper
						title='Customize Header'
						infoMessage='This form is applicable for both blogs and surveys.'
						isLoading={updateHeaderMutation.isPending}
						errors={errors}
						onSubmit={handleSubmit(onValid)}
						onDeleteCustomization={handleDeleteCustomization}
						isDeleting={deleteHeaderMutation.isPending}
						hasCustomization={!!customContent && !!customContent.id}
					>
						<Row className={css(styleSheet.gap24)}>
							<Column className={css(bs.flex1, styleSheet.gap24)}>
								<Row className={css(bs.flex, bs.itemsCenter, bs.gap1)}>
									<h2 className={css(styleSheet.subtitleText)}>
										{logoUrl ? (
											<span>
												Logo
												{isLogoLoading ? (
													<LoadingSpinner type='extra-tiny' className={css(baseStyleSheet.inlineBlock, bs.ml1)} />
												) : (
													<>
														{' ('}
														<button
															type='button'
															className={css(baseStyleSheet.brandLink, styleSheet.logoEditButton)}
															onClick={() => setShowLogoFileInputModal(true)}
														>
															Edit
														</button>
														{' | '}
														<button
															type='button'
															className={css(baseStyleSheet.brandLink, styleSheet.logoEditButton)}
															onClick={() => deleteLogoConfirmationModal.setIsOpen(true)()}
														>
															Remove
														</button>
														{')'}
													</>
												)}
											</span>
										) : (
											'Logo'
										)}
									</h2>
									<InfoPopover message='This logo is account-wide and will appear across all blogs, surveys, and meetings for this account.' />
								</Row>
								{logoUrl ? (
									<img src={logoUrl} alt='Account logo' className={css(styleSheet.accountLogo)} />
								) : (
									<button
										type='button'
										className={css(baseStyleSheet.brandLink)}
										onClick={() => setShowLogoFileInputModal(true)}
									>
										Upload Image
									</button>
								)}
							</Column>
							<Column className={css(bs.flex1, styleSheet.gap24)}>
								<Row className={css(bs.flex, bs.itemsCenter, bs.gap1)}>
									<h2 className={css(styleSheet.subtitleText)}>Font</h2>
									<InfoPopover message='This font will be used as a global default for your blog. You will be able to make further selections for individual components.' />
								</Row>
								<FontSelect
									initialValue={customContent?.overallFont}
									onChange={val => setValue('overallFont', val)}
									value={overallFont}
								/>
							</Column>
							<Column className={css(bs.flex1, styleSheet.gap24)}>
								<h2 className={css(styleSheet.subtitleText)}>Banner</h2>
								<LabeledInput
									error={errors?.mainPageTitle}
									label='Main Page Title'
									placeholder='Blogs'
									{...register('mainPageTitle')}
								/>
								<div className={css(styleSheet.gap8)}>
									<label className={css(styleSheet.label)}>Text Color</label>
									<ColorPicker value={textColor || '#000000'} onChange={color => setValue('textColor', color)} />
								</div>
								<div className={css(styleSheet.gap8)}>
									<label className={css(styleSheet.label)}>Background Color</label>
									<ColorPicker
										value={backgroundColor || '#ffffff'}
										onChange={color => setValue('backgroundColor', color)}
									/>
								</div>
								<div className={css(styleSheet.gap8)}>
									<label className={css(styleSheet.label)}>Hover Color</label>
									<ColorPicker value={hoverColor || '#f0f0f0'} onChange={color => setValue('hoverColor', color)} />
								</div>
							</Column>
							<Column className={css(bs.flex1, styleSheet.gap24)}>
								<h2 className={css(styleSheet.subtitleText)}>Dropdown Menu</h2>
								<div className={css(styleSheet.gap8)}>
									<label className={css(styleSheet.label)}>Text Color</label>
									<ColorPicker
										value={dropdownTextColor || '#000000'}
										onChange={color => setValue('dropdown.color', color)}
									/>
								</div>
								<div className={css(styleSheet.gap8)}>
									<label className={css(styleSheet.label)}>Background Color</label>
									<ColorPicker
										value={dropdownBackgroundColor || '#ffffff'}
										onChange={color => setValue('dropdown.backgroundColor', color)}
									/>
								</div>

								<FontSelect
									initialValue={customContent?.dropdown?.font}
									onChange={val => setValue('dropdown.font', val)}
									value={dropdownFont}
								/>
								<ResponsiveFontSizeInputs
									desktopError={errors?.dropdown?.fontSizePx}
									desktopInputProps={{
										placeholder: getFontSizePlaceholder(dropdownFontSize),
										...register('dropdown.fontSizePx', { setValueAs: parseOptionalFontSize }),
									}}
									desktopLabel='Dropdown Size'
									max={MAX_FONT_SIZE_PX}
									min={MIN_FONT_SIZE_PX}
									mobileError={errors?.dropdown?.mobileFontSizePx}
									mobileInputProps={{
										placeholder: getFontSizePlaceholder(undefined, dropdownFontSize),
										...register('dropdown.mobileFontSizePx', { setValueAs: parseOptionalFontSize }),
									}}
									mobileLabel='Mobile Dropdown Size'
									step={1}
								/>
							</Column>
							<Column className={css(bs.flex2)} />
						</Row>
						<FeatureToggleSection
							enabledFor={enabledFor}
							onEnabledForChange={handleEnabledForChange}
							blogFeatureAvailable={blogFeatureAvailable}
							surveyFeatureAvailable={surveyFeatureAvailable}
							meetingFeatureAvailable={meetingFeatureAvailable}
							disabled={updateHeaderMutation.isPending}
						/>
						<Row className={css(bs.itemsCenter, bs.gap2, bs.mt4)}>
							<Toggle
								id='logo-only-header-toggle'
								isOn={!!logoOnlyHeader}
								onToggleCheckChanged={handleLogoOnlyHeaderChange}
								disabled={hasNonEmptyMenuLinks}
								text='Logo-only header'
							/>
							<InfoPopover message='Turn this on if you want your logo to appear on surveys and meeting pages without configuring menu links or custom colors.' />
						</Row>
						{hasNonEmptyMenuLinks && (
							<div className={css(bs.textSm, bs.textGray600, bs.mt1)}>
								Remove all menu links to enable logo-only header.
							</div>
						)}
						{!logoOnlyHeader && (
							<InputFormTable title='Menu Items'>
								{fields.map((field, index) => (
									<InputFormTableRow<HeaderCustomization>
										collectionName='menuLinks'
										columns={[
											{ fieldName: `label`, placeholder: 'Label' },
											{ fieldName: `destination`, placeholder: 'URL' },
										]}
										key={field.id}
										onDeleteRow={() => remove(index)}
										rowIndex={index}
									>
										<InputFormTableRowChildren<HeaderCustomization, NavLink>
											collectionName='children'
											columns={[
												{ fieldName: 'label', placeholder: 'Label' },
												{ fieldName: 'destination', placeholder: 'URL' },
											]}
											defaultRowValue={{ label: '', destination: '' }}
											parentCollectionName='menuLinks'
											parentRowIndex={index}
										/>
									</InputFormTableRow>
								))}
								<AppendInputRow onAppendRow={() => append(DEFAULT_NAV_LINK_VALUES)} />
							</InputFormTable>
						)}
					</TemplateFormWrapper>
				</FormProvider>
				{showDeleteDialog && (
					<ConfirmationDialog
						icon={<WarningIcon />}
						modalProps={{ isOpen: showDeleteDialog, onRequestClose: handleDeleteConfirm }}
						options={DefaultConfirmationOptions}
						title='Are you sure you want to delete this customization? This action cannot be undone.'
					/>
				)}
				<ImageFileChooserModal
					title='Upload Logo'
					modalProps={{
						isOpen: showLogoFileInputModal,
						onRequestClose: (result, cancel) => {
							if (!cancel && result) {
								uploadLogoMutation.mutate({ logo: result, impersonationContext });
							} else {
								setShowLogoFileInputModal(false);
							}
						},
					}}
				/>
				<ConfirmationDialog
					icon={<WarningIcon />}
					modalProps={deleteLogoConfirmationModal}
					options={DefaultDeleteConfirmationOptions}
					title='Are you sure you want to remove the account logo?'
				/>
			</>
		);
	}
);

HeaderDesignForm.displayName = 'HeaderDesignForm';
